import type { JSX, ReactNode } from 'react'
import type { IPopupProps } from '../Popup'

import { ButtonFonts, ButtonIconSizes, IconPositions } from 'atoms/Button'
import { IconNames } from 'atoms/Icon'
import {
  TextBoxMaxLine3FontLgBold,
  TextBoxMaxLine3FontSm,
} from 'atoms/TextBox/Templates'
import { VoiceSearchLoader } from 'atoms/VoiceSearchLoader'
import { DropdownPosition } from 'blocks/Dropdown'
import {
  DropdownItemColumnGap,
  DropdownItemFonts,
  DropdownItemPaddings,
} from 'blocks/Dropdown/DropdownItems'
import { DropdownItemLanguage } from 'blocks/Dropdown/DropdownItems/DropdownItemTypeButton/Templates/DropdownItemLanguage'
import { DropdownLanguage } from 'blocks/Dropdown/Templates/DropdownLanguage'
import { COLOR } from 'vars/color'
import { FIXED_INDENTS } from 'vars/fixed-indents'
import { Popup } from '../Popup'
import { PopupPaddings, PopupRowGap } from '../Popup.h'
import {
  VoiceSearchContentWrapperSC,
  VoiceSearchFooterButtonSC,
  VoiceSearchFooterSC,
  VoiceSearchHeadSC,
  VoiceSearchPopupSC,
  VoiceSearchResultSC,
} from './VoiceSearchPopup.styled'

export interface IVoiceSearchPopupProps extends Omit<IPopupProps, 'children'> {
  status: string
  result: string
  headChildren?: ReactNode
  languages?: Array<{ id: string; value: string }> | null
  activeLanguageId?: string | null
  onLanguageChange?: (id: string) => void
  isListening?: boolean
  reinitButton?: boolean
  onInitVoiceSearch?: () => void
}

export const VoiceSearchPopup = ({
  padding = PopupPaddings.SIZE_0,
  rowGap = PopupRowGap.INDENT_0,
  hasCloseOnClickOverlay = false,
  status,
  result,
  headChildren,
  languages = null,
  activeLanguageId = null,
  onLanguageChange,
  isListening = false,
  reinitButton = false,
  onInitVoiceSearch,
  ...rest
}: IVoiceSearchPopupProps): JSX.Element => {
  const handleLanguageChange = (id: string) => {
    // Не вызываем stopPropagation, чтобы DropdownBody мог обработать клик и закрыть меню
    onLanguageChange?.(id)
  }

  return (
    <VoiceSearchPopupSC>
      <Popup
        padding={padding}
        rowGap={rowGap}
        hasCloseOnClickOverlay={hasCloseOnClickOverlay}
        popupBackground={COLOR.SURFACE_DARK_4}
        headerBackground={COLOR.SURFACE_DARK_4}
        {...rest}
      >
        <VoiceSearchContentWrapperSC>
          <VoiceSearchHeadSC>
            <TextBoxMaxLine3FontLgBold title={status} />

            {languages && languages.length > 0 && (
              <DropdownLanguage
                title={activeLanguageId ?? ''}
                position={DropdownPosition.Top}
                positionOffset={FIXED_INDENTS.INDENT_20}
                headFont={ButtonFonts.FontSmBold}
                menuHeight={240}
                content={languages}
              >
                {languages.map((language, index) => (
                  <DropdownItemLanguage
                    key={language.id}
                    id={index}
                    title={language.id}
                    activeId={
                      activeLanguageId
                        ? languages.findIndex((l) => l.id === activeLanguageId)
                        : undefined
                    }
                    onClick={() => handleLanguageChange(language.id)}
                    endIcon={
                      language.id === activeLanguageId
                        ? IconNames.selectedFavoriteIcon
                        : undefined
                    }
                    columnGap={DropdownItemColumnGap.INDENT_20}
                    font={DropdownItemFonts.FontSm}
                    paddings={DropdownItemPaddings.SIZE_50}
                    colorDefault={COLOR.TEXT_ICON_SECONDARY}
                    colorSelected={COLOR.TEXT_ICON_PRIMARY}
                    backgroundSelectedDefault={COLOR.TRANSPARENT}
                  />
                ))}
              </DropdownLanguage>
            )}
            {headChildren}
          </VoiceSearchHeadSC>

          {result && (
            <VoiceSearchResultSC>
              <TextBoxMaxLine3FontSm title={result} />
            </VoiceSearchResultSC>
          )}

          <VoiceSearchFooterSC>
            {isListening && <VoiceSearchLoader />}
            {reinitButton && (
              <VoiceSearchFooterButtonSC
                icon={IconNames.microphone}
                iconSize={ButtonIconSizes.mLarge}
                onClick={onInitVoiceSearch}
                iconPosition={IconPositions.Center}
                backgroundColorDefault={COLOR.SURFACE_OPACITY_LIGHT_10}
                backgroundColorHovered={COLOR.SURFACE_OPACITY_LIGHT_10}
              />
            )}
          </VoiceSearchFooterSC>
        </VoiceSearchContentWrapperSC>
      </Popup>
    </VoiceSearchPopupSC>
  )
}
